import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { TabsContent } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Package, Edit2, Trash2, Tag, Building } from "lucide-react";

/**
 * ServicesTab — Pestaña "Servicios" del catálogo.
 *
 * Extraída 1:1 desde `pages/CatalogPage.js`. Renderiza las tarjetas de estadísticas
 * (total de servicios, categorías, proveedores) y el listado de servicios agrupado por
 * categoría en tablas; cada fila expone precios (standalone/paquete con signo por
 * descuento), líneas generadas, permanencia y estado, además de los botones de acción.
 *
 * @param {Array}    props.services            Listado completo de servicios (para el contador).
 * @param {Array}    props.categories          Categorías (para el contador de la tarjeta).
 * @param {Array}    props.providers           Proveedores (para el contador de la tarjeta).
 * @param {Object}   props.groupedServices     Servicios agrupados por categoría { categoria: Service[] }.
 * @param {Function} props.formatCurrency      Formateador de moneda (EUR) del padre.
 * @param {Function} props.setCreateOpen       Abre el modal de "Nuevo Servicio".
 * @param {Function} props.setEditingService   Abre el modal de "Editar Servicio" con el servicio dado.
 * @param {Function} props.canDelete           (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.handleDeleteService (id) => void — elimina un servicio.
 */
export const ServicesTab = ({
  services,
  categories,
  providers,
  groupedServices,
  formatCurrency,
  setCreateOpen,
  setEditingService,
  canDelete,
  handleDeleteService,
}) => {
  return (
        <TabsContent value="services" className="mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#42E695]/20 rounded-lg">
                    <Package className="h-6 w-6 text-[#42E695]" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Total Servicios</p>
                    <p className="text-2xl font-bold text-white">{services.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-[#03BEE4]/20 rounded-lg">
                    <Tag className="h-6 w-6 text-[#03BEE4]" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Categorías</p>
                    <p className="text-2xl font-bold text-white">{categories.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <Building className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Proveedores Activos</p>
                    <p className="text-2xl font-bold text-white">{providers.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services by Category */}
          {Object.keys(groupedServices).length === 0 ? (
            <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No hay servicios en el catálogo</p>
                <Button
                  onClick={() => setCreateOpen(true)}
                  className="mt-4 bg-[#42E695] text-[#04042C]"
                >
                  Crear primer servicio
                </Button>
              </CardContent>
            </Card>
          ) : (
            Object.entries(groupedServices).map(([category, categoryServices]) => (
              <Card key={category} className="bg-[#0F0F3D] border-[#1E1E5F] mb-4">
                <CardHeader>
                  <CardTitle className="text-lg text-[#03BEE4] flex items-center gap-2">
                    <Tag className="h-5 w-5" />
                    {category}
                    <Badge className="ml-2 bg-[#1B1B4B] text-white">{categoryServices.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#1E1E5F]">
                        <TableHead className="text-[#03BEE4]">Nombre</TableHead>
                        <TableHead className="text-[#03BEE4]">Proveedor</TableHead>
                        <TableHead className="text-[#03BEE4]">Precio Standalone</TableHead>
                        <TableHead className="text-[#03BEE4]">Precio Paquete</TableHead>
                        <TableHead className="text-[#03BEE4]">Líneas</TableHead>
                        <TableHead className="text-[#03BEE4]">Permanencia</TableHead>
                        <TableHead className="text-[#03BEE4]">Estado</TableHead>
                        <TableHead className="text-[#03BEE4]">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryServices.map((service) => (
                        <TableRow key={service.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]">
                          <TableCell className="font-medium text-white">{service.nombre}</TableCell>
                          <TableCell className="text-slate-300">{service.proveedor_nombre || "—"}</TableCell>
                          <TableCell className={
                            service.precio_base === null ? "text-slate-500" :
                            service.precio_base < 0 ? "text-red-400 font-semibold" :
                            "text-[#42E695] font-semibold"
                          }>
                            {service.precio_base !== null ? formatCurrency(service.precio_base) : "—"}
                          </TableCell>
                          <TableCell className={
                            service.precio_paquete === null ? "text-slate-500" :
                            service.precio_paquete < 0 ? "text-red-400 font-semibold" :
                            "text-[#03BEE4] font-semibold"
                          }>
                            {service.precio_paquete !== null ? formatCurrency(service.precio_paquete) : "—"}
                          </TableCell>
                          <TableCell>
                            {service.unidades_por_seleccion > 1 && (
                              <Badge className="bg-orange-500/20 text-orange-400">
                                ×{service.unidades_por_seleccion}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-300">{service.permanencia_meses} meses</TableCell>
                          <TableCell>
                            <Badge className={service.is_active ? "bg-[#42E695]/20 text-[#42E695]" : "bg-red-500/20 text-red-400"}>
                              {service.is_active ? "Activo" : "Inactivo"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingService(service)}
                              >
                                <Edit2 className="h-4 w-4 text-[#03BEE4]" />
                              </Button>
                              {canDelete("catalog") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteService(service.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
  );
};
